import React, { useMemo, useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { PaymentOptions } from "@/types";
import {
  SalesReport
} from "@/types/pos/sales-history"
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import {
  capitalizeFirstLetter,
  displayDate,
  displayValue,
  formatDate,
  formatNIC,
  formatToPKR,
  replaceUnderscoreWithSpace,
  saleHistoryMapper,
  hasFullAccessToMemberNIC,
  downloadCSV,
  generateQueryString,
} from "@/lib/helper";
import Pagination from "@/components/ui/table/pagination-table";
import usePagination from "@/hooks/use-pagination";
import { DataTableViewOptions } from "./data-table-view-options";
import TableFilters from "@/components/ui/table/data-table-filter";
import { statusValues } from "@/lib/constants/sale_history";
import { Button } from "@/components/ui/button";
import { useGetAlltransactionQuery, useLazyGetAllTransactionForExportQuery } from "@/services/registerApi";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { Separator } from "@/components/ui/separator";
import { useGetMembersListQuery } from "@/services/memberApi";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { SaleStatus } from "@/types/enums";
import { SellableItem } from "@/types/pos/transaction";
import { useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";

interface SearchCritereaInterface {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  start_date?: string;
  only_failed_srb?: boolean;
  end_date?: string;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "updated_at",
  type: "Sale",
};

export default function SaleshistoryRegisterViewTable() {
  const counter_number =
    useSelector((state: RootState) => state.counter?.counter_number) ?? 0;

  const pos_sale_report = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .pos_sale_report ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const hide_pos_erp = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .hide_pos_erp ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);

  const [openFilter, setOpenFilter] = useState(false);

  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const { data: salesHistoryData, isLoading: salesDataLoading, refetch } =
    useGetAlltransactionQuery(
      {
        // counter_id --> counter_ids
        query: `org_id=${orgId}&counter_ids=${counter_number}${query ? `&${query}` : ''}`,
      },
      {
        skip: query == "",
      }
    );

  const { data: membersData } = useGetMembersListQuery({
    id: Number(orgId),
    query: "",
  });

  const { data: incomeCategoryData } = useGetIncomeCategoryQuery({
    org_id: orgId, query: "sort_key=id&sort_order=desc&status=active"
  }, {
    skip: !orgId || orgId === 0
  });

  const saleshistoryTableData = React.useMemo(() => {
    return Array.isArray(salesHistoryData?.data) ? salesHistoryData?.data : [];
  }, [salesHistoryData]);


  const { toast } = useToast();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "updated_at", desc: true },
  ]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [defaultTab, setDefaultTab] = useState<string>("srb_sale")
  const [selectAllRows, setSelectAllRows] = useState(false);
  const [showSelectAllNotification, setShowSelectAllNotification] = useState(false);
  const [getAllTransactionForExport] = useLazyGetAllTransactionForExportQuery();
  
  const handleExportSelected = async () => {
    if (selectAllRows) {
      // Export all rows
      try {
        // const filterQuery = Object.keys(filterData).length > 0
        //   ? Object.entries(filterData)
        //     .filter(([_, value]) => value !== "")
        //     .map(([key, value]) => `${key}=${value}`)
        //     .join("&")
        //   : "";
        const { limit, offset, ...exportParams } = searchCriteria;
        const filterQuery = generateQueryString(exportParams);

        const result = await getAllTransactionForExport({
          query: `org_id=${orgId}&counter_ids=${counter_number}${filterQuery ? `&${filterQuery}` : ''}`
        }).unwrap();

        console.log("result", { result });

        if (result?.data && result.data.length > 0) {
          downloadCSV(result.data, "all_sales_history.csv", saleHistoryMapper);
          toast({
            variant: "success",
            title: `Exported ${result.data.length} transactions successfully`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "No data to export",
            description: "No transaction records found to export.",
          });
        }
      } catch (error) {
        console.error("Export failed:", error);
        toast({
          variant: "destructive",
          title: "Export failed",
          description: "Failed to export all transactions. Please try again.",
        });
      }
    } else {
      // Export selected rows
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);
      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }
      downloadCSV(selectedRows, "sale_report.csv", saleHistoryMapper);
    }
  };

  const incomeCategoryOptions = useMemo(() => {
    return Array.isArray(incomeCategoryData?.data)
      ? incomeCategoryData?.data.map((item) => ({
        ...item,
        id: item.id?.toString() || "",
        name: item.name,
      }))
      : [];
  }, [incomeCategoryData]);


  const actionsColumn: ColumnDef<SalesReport> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions access={pos_sale_report} data={row?.original} refetch={refetch} />
    ),
  };

  const erpStatusColumn: ColumnDef<SalesReport> = {
    accessorKey: "erp_status",
    meta: "ERP Status",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="ERP Status"
        sortKey="erp_status"
        searchCriteria={searchCriteria}
        toggleSortOrder={toggleSortOrder}
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex gap-2 items-center justify-between w-fit">
          <div className="">
            <p className="capitalize cursor-pointer">
              <span>{displayValue(row.original.erp_status)}</span>
            </p>
          </div>
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
  };

  const cnicNtnColumn: ColumnDef<SalesReport> = {
    accessorKey: "nic",
    meta: "Nic",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="CNIC/NTN"
        sortKey="id"
        sortable={false}
        searchCriteria={searchCriteria}
        toggleSortOrder={toggleSortOrder}
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-nowrap whitespace-nowrap ">
          {displayValue(row?.original.is_business ? row?.original.member_nic : formatNIC(row?.original.member_nic))}
        </div>
      );
    },
  }

  const columns: ColumnDef<SalesReport>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => {
        return (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value: any) => row.toggleSelected(!!value)}
            aria-label="Select row"
            className="translate-y-[2px]"
          />
        );
      },
    },
    {
      accessorKey: "recieptnumber",
      meta: "Reciept Number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Txn Number"
          sortKey="id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            {row.getCanExpand() && (
              <button
                {...{
                  onClick: row.getToggleExpandedHandler(),
                }}
                className="flex gap-1 items-center"
              >
                {row.getIsExpanded() ? (
                  <i className="fa fa-angle-down w-3 h-3"></i>
                ) : (
                  <i className="fa fa-angle-right w-3 h-3"></i>
                )}
              </button>
            )}

            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{displayValue(row.original.receipt_number)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "user",
      meta: "user",

      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="SRB Number"
          sortKey="srb_invoice_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.srb_invoice_id)}
          </div>
        );
      },
    },
    {
      accessorKey: "invoice_number",
      meta: "Invoice Number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Invoice Number"
          sortKey="invoice_number"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.invoice_number)}
          </div>
        );
      },
    },
    {
      accessorKey: "user",
      meta: "user",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Name"
          sortKey="member_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex capitalize items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.member_name)}
          </div>
        );
      },
    },
    ...(hasFullAccessToMemberNIC() ? [cnicNtnColumn] : []),
    {
      accessorKey: "income_category_id",
      meta: "Income Category",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Income Category"
          sortKey="income_category_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2 text-ellipsis whitespace-nowrap overflow-hidden">

            {(() => {
              const uniqueIncomeCategories = Array.from(
                new Set(row.original.items.map((item: SellableItem) => item.income_category_name).filter(Boolean))
              );
              const displayedCategories = uniqueIncomeCategories.slice(0, 2);
              const remainingCount = uniqueIncomeCategories.length - displayedCategories.length;

              return (
                <>
                  {displayedCategories.map((categoryName, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {displayValue(categoryName)}
                    </span>
                  ))}
                  {remainingCount > 0 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      +{remainingCount} more
                    </span>
                  )}
                </>
              );
            })()}
          </div>
        );
      },
    },
    {
      accessorKey: "net_price",
      meta: "Net Price",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Net Price"
          sortKey="net_price"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="text-nowrap whitespace-nowrap ">
            {displayValue(formatToPKR(row?.original.sub_total ?? row?.original.net_price))}
          </div>
        );
      },
    },
    {
      accessorKey: "discountedamount",
      meta: "Discounted Amount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Discount Amount"
          sortKey="discount"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{formatToPKR(row.original.discount)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "subtotal",
      meta: "Subtotal",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Subtotal"
          sortKey="discount"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{formatToPKR(Math.max(0, Number(row.original.net_price ?? row.original.sub_total ?? 0) - Number(row.original.discount ?? 0)))}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "taxamount",
      meta: "Tax amount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Tax Amount"
          sortKey="tax_amount"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {formatToPKR(row?.original.tax_amount)}
          </div>
        );
      },
    },

    {
      accessorKey: "totalamount",
      meta: "total amount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Total Amount"
          sortKey="total"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>
                  {formatToPKR(
                    (row.original.transaction_type == SaleStatus.Refund
                      ? -Number(row.original.total)
                      : row.original.total) as number
                  )}
                </span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{displayValue(row.original.status)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "createdBy",
      meta: "CreatedBy",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Created By"
          sortKey="created_by"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-po  inter">
                      <span>
                        {displayValue(
                          `${row.original.staff_name}`.length > 15
                            ? `${row.original.staff_name}`.substring(0, 15) +
                            "..."
                            : `${row.original.staff_name}`
                        )}
                      </span>
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="capitalize text-sm">
                      {displayValue(`${row?.original?.staff_name}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "date",
      meta: "date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Created Date"
          sortKey="created_at"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer text-nowrap">
                <span>{displayDate(row?.original.created_at)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },

    {
      accessorKey: "updated_at",
      meta: "Updated At",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Updated At"
          sortKey="updated_at"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer text-nowrap">
                <span>{displayDate(row?.original.updated_at)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },

    ...(pos_sale_report !== "read" ? [actionsColumn] : []),
  ];

  // saleshistoryTableData
  const table = useReactTable({
    data: saleshistoryTableData as SalesReport[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowCanExpand: () => true,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  // Handle showing select all notification
  React.useEffect(() => {
    const allPageRowsSelected = table.getIsAllPageRowsSelected();
    const selectedRowCount = table.getSelectedRowModel().rows.length;
    const totalRowsInCurrentPage = table.getRowModel().rows.length;

    if (allPageRowsSelected && selectedRowCount === totalRowsInCurrentPage && totalRowsInCurrentPage > 0 && !selectAllRows) {
      setShowSelectAllNotification(true);
    } else if (selectedRowCount === 0 || selectAllRows) {
      setShowSelectAllNotification(false);
    }
  }, [table.getIsAllPageRowsSelected(), table.getSelectedRowModel().rows.length, selectAllRows]);

  // Reset select all when row selection changes
  React.useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllRows(false);
      setShowSelectAllNotification(false);
    }
  }, [rowSelection]);

  // Auto-select all visible rows when selectAllRows is true and data changes
  React.useEffect(() => {
    if (selectAllRows && saleshistoryTableData.length > 0) {
      table.toggleAllPageRowsSelected(true);
    }
  }, [selectAllRows, saleshistoryTableData, table]);

  const totalRecords = salesHistoryData?.filtered_counts ?? 0;

  const handleSelectAllRows = () => {
    setSelectAllRows(true);
    setShowSelectAllNotification(false);
  };

  const handleClearSelection = () => {
    setSelectAllRows(false);
    setShowSelectAllNotification(false);
    table.toggleAllPageRowsSelected(false);
    setRowSelection({});
  };

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCritereaInterface>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date
      ? formatDate(dates.start_date)
      : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    setFilterData((prev) => ({
      ...prev,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    }));

    setSearchCriteria((prev: any) => ({
      ...prev,
      start_date: formattedStartDate ?? undefined,
      // Only set end_date if start_date is present
      end_date:
        formattedStartDate && !formattedEndDate ? undefined : formattedEndDate,
      offset: 0,
      sort_key: "updated_at",
      sort_order: "desc",
    }));
  };

  function handleFilterChange(field: string, value: string | number) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: statusValues.map((item) => ({
        id: item.value,
        name: item.label,
      })),
      function: (value: string) => handleFilterChange("status", value),
    },

    {
      type: "select",
      name: "member_id",
      label: "Members",
      options: membersData?.map((item) => ({
        id: item.value,
        name: item.label,
      })),
      function: (value: string) => handleFilterChange("member_id", value),
    },
    {
      type: "multiselect",
      name: "income_category_ids",
      label: "Income Category",
      options: incomeCategoryOptions?.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      function: (value: string) => handleFilterChange("income_category_ids", value),
    }
  ];

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3 ">
        <TableSearchFilter
          placeHolder="Search by Txn Number"
          setSearchCriteria={setSearchCriteria}
          inputValue={inputValue} setInputValue={setInputValue}
        />
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          <div className="text-sm  text-black flex items-center gap-1  lg:mb-0 h-8">
            <DatePickerWithRange
              name={"Date Range"}
              value={{
                start_date: filterData.start_date, // Access start_date directly
                end_date: filterData.end_date, // Access end_date directly
              }}
              onValueChange={(dates) => {
                handleDateRange(dates);
              }}
              label={"Select date range"}
              className="w-full" // Ensure full width
            />
          </div>
          <Button
            variant={"outline"}
            className="text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={() => {
              setFilterData({})
              setSearchCriteria(initialValue);
              setDefaultTab("srb_sale")
              setInputValue("")
              setDefaultTab("srb_sale")
            }}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="gap-2 flex justify-center items-center">
                    <i className="fa-solid fa-arrows-rotate"></i>
                    Reset
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset Filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Button>
          <DataTableViewOptions table={table} action={handleExportSelected} />

          <button
            className="border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center"
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i className="fa fa-filter"></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Gmail-style Select All Notification */}
      {(showSelectAllNotification || selectAllRows) && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mx-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-sm text-blue-800">
              {selectAllRows ? (
                <>
                  All <strong>{totalRecords}</strong> transactions are selected.
                </>
              ) : (
                <>
                  All <strong>{table.getSelectedRowModel().rows.length}</strong> transactions on this page are selected.
                </>
              )}
            </div>
            {!selectAllRows && (
              <button
                onClick={handleSelectAllRows}
                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
              >
                Select all {totalRecords} transactions
              </button>
            )}
            {selectAllRows && (
              <button
                onClick={handleClearSelection}
                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
              >
                Clear selection
              </button>
            )}
          </div>
        </div>
      )}

      <div className="w-full flex justify-start items-center">
        <Tabs value={defaultTab} className="relative mr-auto w-full">
          <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
            <TabsTrigger
              value="srb_sale"
              className="relative text-base rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-bold text-muted-foreground shadow-none transition-none focus-visible:ring-0 data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
              onClick={() => {
                setDefaultTab("srb_sale")
                setSearchCriteria(() => ({
                  type: "Sale",
                  sort_key: "updated_at",
                  limit: 10,
                  offset: 0,
                  sort_order: "desc",
                }))
              }}
            >
              Sales
            </TabsTrigger>
            <TabsTrigger
              value="failed_srb"
              className="relative text-base rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-bold text-muted-foreground shadow-none transition-none focus-visible:ring-0 data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
              onClick={() => {
                setDefaultTab("failed_srb")
                setSearchCriteria(() => ({
                  type: "Sale",
                  only_failed_srb: true,
                  sort_key: "updated_at",
                  limit: 10,
                  offset: 0,
                  sort_order: "desc",
                }))
              }}
            >
              Failed SRB Sales
            </TabsTrigger>
            <TabsTrigger
              value="refund"
              className="relative text-base rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-bold text-muted-foreground shadow-none transition-none focus-visible:ring-0 data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
              onClick={() => {
                setDefaultTab("refund")
                setSearchCriteria(() => ({
                  type: SaleStatus.Refund,
                  sort_key: "updated_at",
                  limit: 10,
                  offset: 0,
                  sort_order: "desc",
                }))
              }}
            >
              Refund
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="rounded-none border border-border  ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {" "}
            {/* //overflow-y-auto scrollbar-thin */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {salesDataLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <>
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="h-24">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <>
                        {row.getIsExpanded() && (
                          <TableRow>
                            <TableCell colSpan={columns.length} className="pr-40">
                              <h3 className="text-lg font-bold mb-4">
                                {row.original.transaction_type} Details
                              </h3>

                              <div className="grid grid-cols-12 py-2 gap-4 font-semibold border-b">
                                <span className="col-span-1 text-left">
                                  Name
                                </span>
                                <span className="col-span-2 text-left">
                                  Type
                                </span>
                                <span className="col-span-1 text-left">
                                  Qty
                                </span>
                                <span className="col-span-1 text-left">
                                  Unit Price
                                </span>
                                <span className="col-span-1 text-left">
                                  Discount
                                </span>
                                <span className="col-span-2 text-left">
                                  Tax Amount
                                </span>
                                <span className="col-span-2 text-left">
                                  Sub Total
                                </span>
                                <span className="col-span-1 text-right">
                                  Total
                                </span>
                              </div>

                              <div className="grid grid-cols-12 py-2 gap-4 border-b last:border-b-0">
                                {row.original.items?.map((item, i) => (
                                  <React.Fragment key={i}>
                                    <span className="capitalize col-span-1 text-left">
                                      {item.description} {/* Description */}
                                    </span>

                                    <div className="w-full col-span-2 text-left text-nowrap">
                                      {capitalizeFirstLetter(
                                        replaceUnderscoreWithSpace(
                                          item.item_type
                                        )
                                      )}{" "}
                                      {/* Type */}
                                    </div>

                                    <div className="w-full col-span-1 text-left">
                                      {item.quantity || 0} {/* Quantity */}
                                    </div>

                                    <div className="w-full col-span-1 text-left">
                                      {formatToPKR(
                                        Number(item.price?.toFixed(2))
                                      )}{" "}
                                      {/* Unit Price */}
                                    </div>

                                    <div className="w-full col-span-1 text-left">
                                      {formatToPKR(
                                        Number(item.discount.toFixed(2))
                                      )}{" "}
                                      {/* Discount */}
                                    </div>

                                    <div className="w-full col-span-2 text-left">
                                      {formatToPKR(
                                        Number(item.tax_amount?.toFixed(2))
                                      )}{" "}
                                      {/* Tax Amount */}({item.tax_name}){" "}
                                      {/* Tax Name */}
                                    </div>

                                    <div className="w-full col-span-2 text-left text-nowrap">
                                      {formatToPKR(
                                        Number(
                                          (
                                            (item.quantity || 0) *
                                            (item.price || 0)
                                          ).toFixed(2)
                                        )
                                      )}{" "}
                                      {/* Sub Total */}
                                    </div>

                                    <div className="w-full col-span-2 text-right">
                                      {formatToPKR(
                                        Number(item.total?.toFixed(2))
                                      )}{" "}
                                      {/* Total */}
                                      {item.tax_type} {/* Tax Type */}
                                    </div>
                                  </React.Fragment>
                                ))}
                              </div>

                              <Separator />
                              <div className="grid grid-cols-5 grid-flow-col">
                                <div className="col-span-2">
                                  Notes :
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <p>
                                          <span className="capitalize cursor-pointer">
                                            {displayValue(
                                              `${row.original.notes}`.length >
                                                15
                                                ? `${row.original.notes}`.substring(
                                                  0,
                                                  15
                                                ) + "..."
                                                : `${row.original.notes}`
                                            )}
                                          </span>
                                        </p>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="capitalize text-sm max-w-80">
                                        <p>
                                          {displayValue(
                                            `${row?.original?.notes}`
                                          )}
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <Separator className="w-[50%]" />
                                  {row.original.status == "Paid" && <h4 className="text-lg font-semibold mb-2">
                                    Payment Details
                                  </h4>}
                                  {row.original.status == "Paid" && <div className="grid grid-cols-12 py-2 gap-4 font-semibold">
                                    <span className="col-span-4  text-nowrap">
                                      Payment Method
                                    </span>
                                    <span className="col-span-1">Amount</span>
                                  </div>}
                                  {row.original.payments?.map(
                                    (payment: PaymentOptions, i: number) => (
                                      <div
                                        key={i}
                                        className="grid grid-cols-12 py-2 gap-4 border-b last:border-b-0"
                                      >
                                        <span className="col-span-4">
                                          {displayValue(payment.payment_method)}
                                        </span>
                                        <span className="text-right">
                                          {formatToPKR(payment.amount)}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                                <div className="col-span-3">
                                  <div className="py-4">
                                    <div className="flex justify-between">
                                      <span>Net Price</span>
                                      <span>
                                        {formatToPKR(row.original.net_price ?? row.original.sub_total)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Discount</span>
                                      <span>
                                        {formatToPKR(row.original.discount)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Subtotal</span>
                                      <span>
                                        {formatToPKR(
                                          Math.max(0, Number(row.original.net_price ?? row.original.sub_total ?? 0) - Number(row.original.discount ?? 0))
                                        )}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Total Tax</span>
                                      <span>
                                        {formatToPKR(row.original.tax_amount)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Total</span>
                                      <span>
                                        {formatToPKR(
                                          Number(row.original.total)
                                        )}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Change Given</span>
                                      <span>
                                        {formatToPKR(
                                          Number(row.original.change_amount)
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* <TableRow>
                          <TableCell colSpan={12}>
                            <div className="py-4">
                              <h3 className="text-lg font-bold mb-4">
                                {row.original.transaction_type} Details
                              </h3>
                              <div className="grid grid-cols-12 py-2 gap-4 font-semibold">
                                <span className="col-span-1">Name</span>
                                <span className="col-span-2">Type</span>
                                <span className="col-span-1">Qty</span>
                                <span className="col-span-1">Price</span>
                                <span className="col-span-1">Discount</span>
                                <span className="col-span-1">Tax Amount</span>
                                <span className="col-span-1">Tax Type</span>
                                <span className="col-span-1">Tax (Rate)</span>
                                <span className="col-span-1">Sub Total</span>
                                <span className="col-span-1">Total</span>
                                <span className="col-span-1">Notes</span>
                              </div>
                              {row.original.items?.map(
                                (item: lineItems, i: number) => (
                                  <div
                                    key={i}
                                    className="grid grid-cols-12 py-2 gap-4 border-b last:border-b-0"
                                  >
                                    <span className="capitalize col-span-1">
                                      {item.description}
                                    </span>

                                    <span className="col-span-2 text-nowrap">
                                      {capitalizeFirstLetter(
                                        replaceUnderscoreWithSpace(
                                          item.item_type
                                        )
                                      )}
                                    </span>

                                    <span className="col-span-1">
                                      {item.quantity}
                                    </span>
                                    <span className="col-span-1">
                                      {formatToPKR(item.price)}
                                    </span>
                                    <span className="col-span-1">
                                      {formatToPKR(item.discount)}
                                    </span>
                                    <span className="col-span-1  ">
                                      {formatToPKR(item.tax_amount)}
                                    </span>

                                    <span className="col-span-1 w-20">
                                      {item.tax_type}
                                    </span>
                                    <span className="capitalize col-span-1 w-20 text-nowrap">
                                      {`${item.tax_name} (${item.tax_rate.toFixed(2)}%)`}
                                    </span>
                                    <span className="col-span-1">
                                      {formatToPKR(item.sub_total)}
                                    </span>
                                    <span className="col-span-1">
                                      {formatToPKR(
                                        (row.original.transaction_type ==
                                        "Refund"
                                          ? -Number(item.total)
                                          : item.total) as number
                                      )}
                                    </span>
                                    <div className="col-span-1">
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <p>
                                              <span className="capitalize cursor-pointer">
                                                {displayValue(
                                                  `${row.original.notes}`
                                                    .length > 15
                                                    ? `${row.original.notes}`.substring(
                                                        0,
                                                        15
                                                      ) + "..."
                                                    : `${row.original.notes}`
                                                )}
                                              </span>
                                            </p>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p className="capitalize text-sm">
                                              {displayValue(
                                                `${row?.original?.notes}`
                                              )}
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </div>
                                  </div>
                                )
                              )}
                              <div className="grid grid-cols-12  mt-4"></div>
                            </div>
                            <h4 className="text-lg font-semibold mb-2">
                              Payment Details
                            </h4>
                            <div className="grid grid-cols-12 py-2 gap-4 font-semibold">
                              <span className="col-span-2  text-nowrap">
                                Payment Method
                              </span>
                              <span className="col-span-1">Amount</span>
                            </div>
                            {row.original.payments?.map(
                              (payment: PaymentOptions, i: number) => (
                                <div
                                  key={i}
                                  className="grid grid-cols-12 py-2 gap-4 border-b last:border-b-0"
                                >
                                  <span className="col-span-2">
                                    {displayValue(payment.payment_method)}
                                  </span>
                                  <span className="text-right">
                                    {formatToPKR(payment.amount)}
                                  </span>
                                </div>
                              )
                            )}
                          </TableCell>
                        </TableRow> */}
                      </>
                    )}
                  </>
                ))
              ) : saleshistoryTableData.length > 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No sale history found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {
        saleshistoryTableData.length > 0 && (
          <Pagination
            limit={searchCriteria.limit}
            offset={searchCriteria.offset}
            totalItems={totalRecords}
            onLimitChange={handleLimitChange}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
            onFirstPage={handleFirstPage}
            onLastPage={handleLastPage}
            isLastPage={isLastPage}
          />
        )
      }

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
        onReset={() => {
          setFilterData({});
          setSearchCriteria(initialValue);
          setInputValue("");
          setDefaultTab("srb_sale")
        }}
      />
    </div >
  );
}
